import {
  ChangeEvent,
  FC,
  createElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { QuestionDropdownModel, RendererFactory } from "survey-core";
import { ReactQuestionFactory } from "survey-react-ui";
import { Combobox } from "../ui/combobox";
import { useTranslate } from "@tolgee/react";

const SPECIFY_OTHER_PLACEHOLDER = "specify-other-placeholder";

interface ComboboxQuestionProps {
  isDisplayMode: boolean;
  question: QuestionDropdownModel;
}

export const ComboboxQuestion: FC<ComboboxQuestionProps> = ({ question }) => {
  const {
    errors,
    value,
    isReadOnly,
    name,
    allowClear,
    isRequired,
    placeholder,
    otherItem,
    noneItem,
    hasOther,
    hasNone,
    isOtherSelected,
    commentPlaceHolder,
    cssClasses,
  } = question;

  const [textAreaInput, setTextAreaInput] = useState(question.comment);
  const { t } = useTranslate();
  const isDisabled = useMemo(() => {
    return isReadOnly;
  }, [isReadOnly]);

  const onValueChange = useCallback(
    (newVal: string) => {
      question.value = newVal;
    },
    [question]
  );

  const onCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaInput(event.target.value);
  };

  const onCommentBlur = () => {
    question.comment = textAreaInput;
  };

  const getItems = useMemo(() => {
    const items = question.dataChoices.map(({ title, value }) => ({
      title,
      value,
    }));
    if (hasNone) items.push(noneItem);
    if (hasOther) items.push(otherItem);
    if (allowClear) items.unshift({ value: undefined, title: "" });
    return items;
  }, [
    question.dataChoices,
    hasNone,
    noneItem,
    hasOther,
    otherItem,
    allowClear,
  ]);

  return (
    <>
      <Combobox
        hasError={errors?.length > 0}
        cssClasses={cssClasses}
        aria-required={question.ariaRequired}
        aria-label={question.ariaLabel}
        aria-invalid={question.ariaInvalid}
        aria-errormessage={question.ariaErrormessage}
        id={question.inputId}
        items={getItems}
        required={isRequired}
        disabled={isDisabled}
        value={value}
        name={name}
        onValueChange={onValueChange}
        placeholder={placeholder}
      />
      {isOtherSelected && (
        <div className={cssClasses.commentArea}>
          <textarea
            disabled={isDisabled}
            onChange={onCommentChange}
            onBlur={onCommentBlur}
            className={cssClasses.comment}
            value={textAreaInput}
            placeholder={commentPlaceHolder || t(SPECIFY_OTHER_PLACEHOLDER)}
          />
        </div>
      )}
    </>
  );
};

ReactQuestionFactory.Instance.registerQuestion(
  "sv-combobox-question",
  function (props: any) {
    return createElement(ComboboxQuestion, props);
  }
);

RendererFactory.Instance.registerRenderer(
  "dropdown",
  "combobox-question",
  "sv-combobox-question"
);
